import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Heading } from 'components';
import { formatDownloadOpening } from './downloadOpening';
import {
  CountdownWrapper,
  CountdownIntro,
  CountdownBooks,
  CountdownSubtitle,
  CountdownUnits,
  CountdownUnit,
  CountdownValue,
  CountdownLabel,
} from './BookCountdown.styled';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const pad = (value) => String(value).padStart(2, '0');

// Counts down to one download opening, then calls onFinish so the list is fetched
// again - bringing the files that just opened and, if another is scheduled, the next
// opening to count down to.
const BookCountdown = ({ opening, onFinish }) => {
  const { opensAt, downloadableFrom, books } = opening;
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= opensAt) {
        clearInterval(timer);
        onFinish();
      }
    }, SECOND);

    return () => clearInterval(timer);
  }, [opensAt, onFinish]);

  // Holds at zero until the fresh list swaps in the next opening (or none), so the
  // panel does not blink out in between.
  const left = Math.max(0, opensAt - now);

  const units = [
    { key: 'days', value: Math.floor(left / DAY) },
    { key: 'hours', value: Math.floor(left / HOUR) % 24 },
    { key: 'minutes', value: Math.floor(left / MINUTE) % 60 },
    { key: 'seconds', value: Math.floor(left / SECOND) % 60 },
  ];
  const titles = books.map((book) => book.title).filter(Boolean);

  return (
    <CountdownWrapper>
      <CountdownIntro>
        <Heading as="h3">{t('page.user.myLibrary.countdown.title')}</Heading>
        {titles.length > 0 && <CountdownBooks>{titles.join(' · ')}</CountdownBooks>}
        <CountdownSubtitle>
          {t('page.user.myLibrary.countdown.subtitle', {
            date: formatDownloadOpening(downloadableFrom),
          })}
        </CountdownSubtitle>
      </CountdownIntro>
      <CountdownUnits role="timer">
        {units.map(({ key, value }) => (
          <CountdownUnit key={key}>
            <CountdownValue>{pad(value)}</CountdownValue>
            <CountdownLabel>{t(`page.user.myLibrary.countdown.${key}`)}</CountdownLabel>
          </CountdownUnit>
        ))}
      </CountdownUnits>
    </CountdownWrapper>
  );
};

BookCountdown.propTypes = {
  opening: PropTypes.shape({
    opensAt: PropTypes.number.isRequired,
    downloadableFrom: PropTypes.string.isRequired,
    books: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.string })).isRequired,
  }).isRequired,
  onFinish: PropTypes.func,
};

BookCountdown.defaultProps = {
  onFinish: () => {},
};

export default BookCountdown;
